// GET /api/objects/:object: one page of an ops table, searched, filtered and
// sorted on the server; GET /api/objects/:object/meta: its filterable fields.
// Request building, filter translation and response parsing live here, free of
// auth and React, so they can be unit tested (objectsApi.test.ts); the calls
// themselves go through authenticatedFetch in ObjectListPage.

import {
  type FilterFieldDef,
  type FilterRow,
  isRelativeDateToken,
  resolveRelativeDate,
} from '@olytic/foundation';

import type { ObjectKey } from '../navigation';
import { readRelations, readWritable, type Relation, type Writable } from './writes';

/** A row as the API returns it: snake_case column names, a uuid `id`. */
export type ObjectRow = { id: string } & Record<string, unknown>;

/** One condition as the API takes it (see apps/server/src/filters.mjs). */
export interface ApiFilter {
  field: string;
  operator: string;
  value?: string | boolean | string[];
}

export interface ObjectListQuery {
  q?: string;
  sort?: string;
  dir?: 'asc' | 'desc';
  /** Conditions, numbered from 1 in `logic`. */
  filters?: ApiFilter[];
  /** e.g. "1 AND (2 OR 3)"; blank means every condition must hold. */
  logic?: string;
  /** Table columns the object does not return unless named (/meta marks them `extra`). */
  columns?: string[];
  /** Only the records the caller opened (Recently viewed), most recent first unless sorted. */
  recent?: boolean;
  limit: number;
  offset: number;
}

export interface ObjectListPage {
  rows: ObjectRow[];
  total: number;
}

/** A failed list request, with the server's message when it sent one. */
export class ObjectsApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** The server's error body, for errors the UI handles (e.g. `code: 'account_size_required'`). */
    readonly body: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ObjectsApiError';
  }
}

/** The path and query for one page. Empty search and missing sort are left out. */
export function objectListUrl(object: ObjectKey, query: ObjectListQuery): string {
  const params = new URLSearchParams();
  const q = query.q?.trim() ?? '';
  if (q !== '') params.set('q', q);
  if (query.sort) {
    params.set('sort', query.sort);
    params.set('dir', query.dir ?? 'asc');
  }
  if (query.filters && query.filters.length > 0) {
    params.set('filters', JSON.stringify(query.filters));
    const logic = query.logic?.trim() ?? '';
    if (logic !== '') params.set('logic', logic);
  }
  if (query.columns && query.columns.length > 0) params.set('columns', query.columns.join(','));
  if (query.recent) params.set('recent', '1');
  params.set('limit', String(query.limit));
  params.set('offset', String(query.offset));
  return `/api/objects/${encodeURIComponent(object)}?${params.toString()}`;
}

/** The list's matrix (GET /api/objects/:object/matrix): the same search, filters and Recently viewed, grouped by two fields. */
export function objectMatrixUrl(
  object: ObjectKey,
  query: Omit<ObjectListQuery, 'limit' | 'offset' | 'sort' | 'dir' | 'columns'>,
  matrix: { rowKey: string; columnKey: string; figureIds: string[] },
): string {
  const params = new URLSearchParams();
  const q = query.q?.trim() ?? '';
  if (q !== '') params.set('q', q);
  if (query.filters && query.filters.length > 0) {
    params.set('filters', JSON.stringify(query.filters));
    const logic = query.logic?.trim() ?? '';
    if (logic !== '') params.set('logic', logic);
  }
  if (query.recent) params.set('recent', '1');
  params.set('rows', matrix.rowKey);
  params.set('columns', matrix.columnKey);
  params.set('figures', matrix.figureIds.join(','));
  return `/api/objects/${encodeURIComponent(object)}/matrix?${params.toString()}`;
}

export type MatrixCells = Record<string, Record<string, number | null>>;

/** The matrix's cells, or throws an ObjectsApiError carrying its status. */
export async function parseObjectMatrixResponse(response: Response): Promise<MatrixCells> {
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }
  if (!response.ok) {
    const message = isRecord(payload) && typeof payload.error === 'string' && payload.error !== '' ? payload.error : `The matrix could not be loaded (${response.status}).`;
    throw new ObjectsApiError(response.status, message);
  }
  if (!isRecord(payload) || !isRecord(payload.cells)) throw new ObjectsApiError(response.status, 'The matrix response was not in the expected shape.');
  return payload.cells as MatrixCells;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Turns the response into rows and a total, or throws an ObjectsApiError carrying its status. */
export async function parseObjectListResponse(response: Response): Promise<ObjectListPage> {
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }

  if (!response.ok) {
    const message =
      isRecord(payload) && typeof payload.error === 'string' && payload.error.length > 0
        ? payload.error
        : `The records could not be loaded (${response.status}).`;
    throw new ObjectsApiError(response.status, message);
  }

  if (
    !isRecord(payload) ||
    !Array.isArray(payload.rows) ||
    typeof payload.total !== 'number' ||
    !payload.rows.every((row) => isRecord(row) && typeof row.id === 'string')
  ) {
    throw new ObjectsApiError(response.status, 'The records response was not in the expected shape.');
  }
  return { rows: payload.rows as ObjectRow[], total: payload.total };
}

// ── Filters ───────────────────────────────────────────────────────────────

/** The server's caps (GET /meta also reports them). */
export const MAX_FILTERS = 20;

/**
 * Builder rows as API conditions. A relative date ("last 30 days") becomes a
 * range resolved against `today`, so a saved view keeps meaning "last 30 days";
 * a boolean value is sent as a boolean.
 */
export function toApiFilters(rows: FilterRow[], fields: FilterFieldDef[], today: string): ApiFilter[] {
  return rows.map((row) => {
    const field = fields.find((f) => f.key === row.field);
    if (field?.kind === 'date' && isRelativeDateToken(row.value)) {
      const range = resolveRelativeDate(row.value, today);
      if (range) {
        switch (row.operator) {
          case 'equals':
            return { field: row.field, operator: 'between', value: [range.from, range.to] };
          case 'not_equals':
            return { field: row.field, operator: 'not_between', value: [range.from, range.to] };
          case 'greater_than':
            return { field: row.field, operator: 'greater_than', value: range.to };
          case 'less_than':
            return { field: row.field, operator: 'less_than', value: range.from };
          default:
            break;
        }
      }
    }
    // The builder's extended operators: no value, or a list of values.
    if (row.operator === 'is_empty' || row.operator === 'is_not_empty') return { field: row.field, operator: row.operator };
    if (['between', 'not_between', 'in', 'not_in'].includes(row.operator)) {
      return { field: row.field, operator: row.operator, value: row.values ?? [] };
    }
    if (field?.kind === 'boolean') return { field: row.field, operator: row.operator, value: row.value === 'true' };
    return { field: row.field, operator: row.operator, value: row.value };
  });
}

/**
 * The checks the server makes that the library's builder does not: every
 * filter must appear in the logic, and there may be at most MAX_FILTERS
 * conditions in all. Returns the problem, or null.
 */
export function filterProblem(rows: FilterRow[], logic: string): string | null {
  if (rows.length > MAX_FILTERS) {
    return `Use at most ${MAX_FILTERS} filters.`;
  }
  const trimmed = logic.trim();
  if (trimmed === '') return null;
  const used = new Set((trimmed.match(/\d+/g) ?? []).map(Number));
  const missing = rows.map((_, i) => i + 1).filter((n) => !used.has(n));
  if (missing.length > 0) {
    return `Filter logic must use every filter; add ${missing.join(', ')}.`;
  }
  return null;
}

// ── Metadata ──────────────────────────────────────────────────────────────

export type MetaFieldType =
  | 'text' | 'number' | 'currency' | 'percent' | 'date' | 'datetime' | 'boolean' | 'picklist';

export interface ObjectMetaField {
  key: string;
  type: MetaFieldType;
  operators: string[];
  /** Picklist values: the schema's, or the most common in the data. */
  options?: string[];
}

/**
 * A column a list may show; `extra` ones come back only when the list names them.
 * A `recordOnly` one (long text) no list shows: only the record page reads it.
 */
export interface ObjectMetaColumn {
  key: string;
  type: MetaFieldType;
  extra?: boolean;
  recordOnly?: boolean;
  /** A lookup: the related record's name, linked to its page through `idKey`. */
  lookup?: { object: string; idKey: string };
}

export interface ObjectMeta {
  fields: ObjectMetaField[];
  /** Every column of the object's table the caller may see; absent from an older server. */
  columns?: ObjectMetaColumn[];
  /** What people may change, create and delete; absent or malformed reads as nothing. */
  writable?: Writable;
  /** What each related list can add and remove, by relation key. */
  relations?: Record<string, Relation>;
}

export function objectMetaUrl(object: ObjectKey): string {
  return `/api/objects/${encodeURIComponent(object)}/meta`;
}

const META_TYPES = new Set(['text', 'number', 'currency', 'percent', 'date', 'datetime', 'boolean', 'picklist']);

/** Turns the /meta response into fields, or throws an ObjectsApiError. */
export async function parseObjectMetaResponse(response: Response): Promise<ObjectMeta> {
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }
  if (!response.ok) {
    const message =
      isRecord(payload) && typeof payload.error === 'string' && payload.error.length > 0
        ? payload.error
        : `The fields could not be loaded (${response.status}).`;
    throw new ObjectsApiError(response.status, message);
  }
  const fieldsOk =
    isRecord(payload) &&
    Array.isArray(payload.fields) &&
    payload.fields.every(
      (f) =>
        isRecord(f) &&
        typeof f.key === 'string' &&
        typeof f.type === 'string' &&
        META_TYPES.has(f.type) &&
        Array.isArray(f.operators) &&
        (f.options === undefined || (Array.isArray(f.options) && f.options.every((o) => typeof o === 'string'))),
    );
  if (!fieldsOk) throw new ObjectsApiError(response.status, 'The fields response was not in the expected shape.');
  return {
    fields: (payload as { fields: ObjectMetaField[] }).fields,
    columns: readColumns(payload),
    writable: readWritable(payload),
    relations: readRelations(payload),
  };
}

/** /meta's columns; anything malformed is left out. */
function readColumns(payload: unknown): ObjectMetaColumn[] {
  if (!isRecord(payload) || !Array.isArray(payload.columns)) return [];
  return payload.columns.flatMap((c): ObjectMetaColumn[] =>
    isRecord(c) && typeof c.key === 'string' && typeof c.type === 'string' && META_TYPES.has(c.type)
      ? [{
          key: c.key,
          type: c.type as MetaFieldType,
          ...(c.extra === true ? { extra: true } : {}),
          ...(c.recordOnly === true ? { recordOnly: true } : {}),
          ...(isRecord(c.lookup) && typeof c.lookup.object === 'string' && typeof c.lookup.idKey === 'string'
            ? { lookup: { object: c.lookup.object, idKey: c.lookup.idKey } }
            : {}),
        }]
      : [],
  );
}

/** Today in Olytic's time zone as YYYY-MM-DD, for resolving relative dates. */
export function todayIso(now = new Date()): string {
  return now.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

// ── Records and related lists ─────────────────────────────────────────────

/** GET /api/objects/:object/:id — one record. */
export function recordUrl(object: ObjectKey, id: string): string {
  return `/api/objects/${encodeURIComponent(object)}/${encodeURIComponent(id)}`;
}

export interface RelatedQuery {
  limit: number;
  offset: number;
  sort?: string;
  dir?: 'asc' | 'desc';
  /** The quick filter: rows with any shown value containing this text. */
  q?: string;
}

/** GET /api/objects/:object/:id/related/:relation — one page of a related list. */
export function relatedUrl(object: ObjectKey, id: string, relation: string, query: RelatedQuery): string {
  const params = new URLSearchParams();
  if (query.sort) {
    params.set('sort', query.sort);
    params.set('dir', query.dir ?? 'asc');
  }
  if (query.q?.trim()) params.set('q', query.q.trim());
  params.set('limit', String(query.limit));
  params.set('offset', String(query.offset));
  return `${recordUrl(object, id)}/related/${encodeURIComponent(relation)}?${params.toString()}`;
}

/** Turns the record response into the record, or throws an ObjectsApiError (404 for a record that isn't there). */
export async function parseRecordResponse(response: Response): Promise<ObjectRow> {
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }
  if (!response.ok) {
    const message =
      isRecord(payload) && typeof payload.error === 'string' && payload.error.length > 0
        ? payload.error
        : `The record could not be loaded (${response.status}).`;
    throw new ObjectsApiError(response.status, message);
  }
  if (!isRecord(payload) || typeof payload.id !== 'string') {
    throw new ObjectsApiError(response.status, 'The record response was not in the expected shape.');
  }
  return payload as ObjectRow;
}
